import { useSnackbar } from 'notistack';
import { useState, useCallback } from 'react';
import { m, AnimatePresence } from 'framer-motion';

import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Card,
  Link,
  Stack,
  Button,
  Dialog,
  Divider,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  useMediaQuery
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { APP_SIDESHEET_Z_INDEX } from 'src/theme/z-index';
import { TaskStatus } from 'src/services/tasks/tasks.types';
import { useGetTaskQuery, useMarkTaskCompletedMutation } from 'src/services/tasks/tasks.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import DateDisplay from 'src/components/date/date-display';
import TaskPriorityLabel from 'src/components/tasks/task-priority-label';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  taskId: string
};

export default function TaskViewer({ open, onClose, taskId }: Props) {

  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate();

  const router = useRouter();

  const tenantType = useOrgTenant();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isClosing, setIsClosing] = useState(false);

  // custom close handler to ensure animation completes
  const handleClose = useCallback(() => {
    if (!isMobile) {
      setIsClosing(true);
      // alow time for exit animation to complete
      setTimeout(() => {
        onClose();
        setIsClosing(false);
      }, 400); // Match exit animation duration
    } else {
      // for mobile, close immediately
      onClose();
    }
  }, [isMobile, onClose]);

  const handleViewAll = useCallback(() => {
    router.push(paths.dashboard.tasks.root());
  }, [router]);

  const { currentData: task } = useGetTaskQuery({
    taskId: taskId as string
  }, {
    skip: !taskId
  });

  const [markTaskCompleted, { isLoading: isUpdating }] = useMarkTaskCompletedMutation();

  const handleMarkComplete = useCallback(async () => {
    if (!task) return;

    try {
      await markTaskCompleted({
        taskId: task.id
      }).unwrap();

      enqueueSnackbar(t('tasks.api.complete.success'), { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar(t('tasks.api.default_error'), { variant: 'error' });
    }
  }, [task, markTaskCompleted, enqueueSnackbar, t, handleClose]);

  if (!task) return null;

  const renderTaskDetails = () => (
    <>
      <Box sx={{ mb: 2 }}>

        <TruncatedTextField
          text={`${task.title}`}
          mode="lines"
          gutterBottom
          limit={1}
          showMoreEnabled={false}
          variant="h6"
          style={{ textTransform: 'capitalize' }}
        />

        {
          task.due_at && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Iconify icon="mdi:clock-outline" width={16} height={16} />
              <Typography variant="body2">
                {task.due_at ? (
                  <DateDisplay date={task.due_at} format="DD MMM YYYY, hh:mm A" variant='body2' />
                ) : (
                  t('common.not_set')
                )}
              </Typography>
            </Stack>
          )
        }

        {
          task.contact && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon="hugeicons:user" width={16} height={16} />
              <Link
                component={RouterLink}
                noWrap
                href={paths.dashboard.contacts.detail(task.contact.id)}
              >
                <TruncatedTextField
                  text={`${task.contact.first_name} ${task.contact.last_name}`}
                  mode="characters"
                  limit={30}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          )
        }

        {
          task.organization_candidate && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon="mdi:account-tie-outline" width={16} height={16} />
              <Link
                component={RouterLink}
                noWrap
                href={paths.dashboard.candidates.detail(task.organization_candidate.id)}
              >
                <TruncatedTextField
                  text={`${task.organization_candidate.candidate_profile?.first_name ?? ''} ${task.organization_candidate.candidate_profile?.last_name ?? ''}`}
                  mode="characters"
                  limit={30}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          )
        }

        {
          task.company_profile && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon="uil:building" width={16} height={16} />
              <Link
                component={RouterLink}
                noWrap
                href={paths.dashboard.companies.detail(task.company_profile.id, tenantType, task.company_profile.company_type)}
              >
                <TruncatedTextField
                  text={`${task.company_profile.company_name}`}
                  mode="characters"
                  limit={30}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          )
        }

        {
          task.deal && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon="uil:building" width={16} height={16} />
              <Link
                component={RouterLink}
                noWrap
                href={paths.dashboard.deal_flow.deals.detail(task.deal.id)}
              >
                <TruncatedTextField
                  text={`${task.deal.title}`}
                  mode="characters"
                  limit={30}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          )
        }

        {
          task.job && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon="mdi:briefcase-outline" width={16} height={16} />
              <Link
                component={RouterLink}
                noWrap
                href={paths.dashboard.jobs.detail(task.job.id)}
              >
                <TruncatedTextField
                  text={`${task.job.title}`}
                  mode="characters"
                  limit={30}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          )
        }

        {
          task.job_application && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon="mdi:file-document-outline" width={16} height={16} />
              <Link
                component={RouterLink}
                noWrap
                href={paths.dashboard.jobs.application(task.job_application.job.id, task.job_application.id)}
              >
                <TruncatedTextField
                  text={`${task.job_application.job.title} - ${task.job_application.attached_profile.first_name} ${task.job_application.attached_profile.last_name}`}
                  mode="characters"
                  limit={30}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          )
        }

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Iconify icon="mdi:flag-outline" width={16} height={16} />
            <TaskPriorityLabel priority={task.priority} />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Iconify icon="tabler:calendar-clock" width={16} height={16} />
            <Label color={task.status === TaskStatus.COMPLETED ? 'success' : 'default'}>
              {t(`enums.task_status.${task.status}`)}
            </Label>
          </Stack>
        </Stack>

      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={2}>
        <Typography variant="subtitle2">
          {t('tasks.viewer.description')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify icon="mdi:tag-outline" width={16} height={16} />
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {task.type.replace('_', ' ')}
          </Typography>
        </Stack>

      </Stack>

      <Box sx={{ overflow: 'auto', maxHeight: 300, mb: 2 }}>
        <TruncatedTextField
          text={task.description || ''}
          mode="lines"
          limit={5}
          showMoreEnabled
          variant="body2"
          color="text.secondary"
          style={{ whiteSpace: 'pre-wrap' }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

    </>
  );

  // mobile view - modal dialog
  if (isMobile) {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {t('tasks.viewer.title')}
            </Typography>

            <Stack direction="row" alignItems="center">
              <IconButton
                edge="end"
                onClick={handleViewAll}
              >
                <Iconify icon="ic:outline-view-day" />
              </IconButton>
              <IconButton
                edge="end"
                onClick={handleClose}
              >
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {renderTaskDetails()}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            {t('common.close')}
          </Button>

          {task.status !== TaskStatus.COMPLETED && (
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isUpdating}
              onClick={handleMarkComplete}
              startIcon={<Iconify icon="mdi:check-circle-outline" />}
            >
              {t('tasks.actions.complete')}
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  // desktop view - Floating card
  return (
    <AnimatePresence mode="wait">
      {(open || isClosing) && (
        <m.div
          key="task-viewer"
          data-app-sidesheet
          initial={{ y: 100, opacity: 0 }}
          animate={isClosing ? { y: 100, opacity: 0 } : { y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
            exit: { duration: 0.4 } // slightly slower exit animation
          }}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            // Same app-root layer as the mailer; at 1000 it opened behind the quick view (JT1-1583).
            zIndex: APP_SIDESHEET_Z_INDEX,
            maxWidth: 360,
            width: '100%',
          }}
        >
          <Card sx={{ boxShadow: theme.customShadows.z20 }}>
            <Box sx={{ position: 'relative', p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ pr: 4 }}>
                  {t('tasks.viewer.title')}
                </Typography>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    edge="end"
                    size="small"
                    sx={{ p: 0 }}
                    onClick={handleViewAll}
                  >
                    <Iconify icon="ic:outline-view-day" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ p: 0 }}
                    onClick={handleClose}
                  >
                    <Iconify icon="eva:close-fill" />
                  </IconButton>
                </Stack>
              </Stack>

              <Divider sx={{ mt: 1, mb: 2 }} />

              {renderTaskDetails()}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>

                {task.status !== TaskStatus.COMPLETED && (
                  <LoadingButton
                    sx={{ width: '100%' }}
                    variant="contained"
                    color="primary"
                    loading={isUpdating}
                    onClick={handleMarkComplete}
                    startIcon={<Iconify icon="mdi:check-circle-outline" />}
                  >
                    {t('tasks.actions.complete')}
                  </LoadingButton>
                )}
              </Box>
            </Box>
          </Card>
        </m.div>
      )}
    </AnimatePresence>
  );
}
